import { FormHelperText } from '@mui/material';
import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { StyledTextField } from '@/components/common/forms/FormikFields/input.styles';
import useDateRangeInputLogic from '@/components/common/Inputs/DateRangeInput/useDateRangeInputLogic';
import { DatePickerRange } from '@/domain/requestsPage';
import { COLOR, MEDIA_QUERIES_BREAKPOINTS } from '@/globalStyles';
import { getDatePlaceholder } from '@/utils/date/date';

interface IDateRangeInputProps {
    className?: string;
    name: string;
    dateRange: DatePickerRange;
    onChange: (name: string, dateRange: DatePickerRange) => void;
    onValidationChange?: (isValid: boolean) => void;
    error?: boolean;
    disabled?: boolean;
}

const DateRangeInput = ({
    dateRange,
    onChange,
    onValidationChange,
    name,
    error,
    disabled,
    className,
}: IDateRangeInputProps) => {
    const { t } = useTranslation();

    const {
        startDate,
        endDate,
        isStartDateValid,
        isEndDateValid,
        onStartDateChange,
        onEndDateChange,
        onStartDateBlur,
        onEndDateBlur,
    } = useDateRangeInputLogic({ dateRange, onChange, name });

    const datePlaceholder = getDatePlaceholder();

    useEffect(() => {
        onValidationChange?.(isStartDateValid && isEndDateValid);
    }, [isStartDateValid, isEndDateValid, onValidationChange]);

    return (
        <div className={className}>
            <div>
                <StyledTextField
                    name="startDate"
                    value={startDate}
                    onChange={onStartDateChange}
                    onBlur={onStartDateBlur}
                    label={t('forms_date_input_from')}
                    placeholder={datePlaceholder}
                    error={error || !isStartDateValid}
                    disabled={disabled}
                    size="small"
                />
                <Dash>
                    <>&mdash;</>
                </Dash>
                <StyledTextField
                    name="endDate"
                    value={endDate}
                    onChange={onEndDateChange}
                    onBlur={onEndDateBlur}
                    label={t('forms_date_input_to')}
                    placeholder={datePlaceholder}
                    error={error || !isEndDateValid}
                    disabled={disabled}
                    size="small"
                />
            </div>

            {!isEndDateValid || !isStartDateValid ? (
                <FormHelperText disabled={disabled} error>
                    {t('forms_date_input_invalid_format', { format: datePlaceholder })}
                </FormHelperText>
            ) : null}
        </div>
    );
};

const Dash = styled.div`
    margin: 0 0.5rem;
    color: ${COLOR.darkGray};
`;

const Memoized = memo(DateRangeInput);
const Styled = styled(Memoized)`
    & > div {
        display: flex;
        align-items: center;
        ${StyledTextField} {
            width: 9rem;

            @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.tablet}px) {
                width: 100%;
            }
        }
    }
    .Mui-disabled {
        cursor: not-allowed;
    }
`;

export default Styled;
